// Display-only price formatting (DELOWO_DESIGN §9). Used by cards, map labels and panels that show a
// price as a headline. Receipts, checkout and transaction amounts keep formatMoney (2 decimals).

/**
 * Money for display: whole amounts without decimals («180 €»), amounts with cents with 2 decimals
 * («45,50 €»). The global formatMoney is not changed.
 *
 * @param {Object} intl react-intl
 * @param {Object} money Money ({ amount in subunits, currency })
 * @returns {string} formatted price, or '' without a usable amount
 */
export const formatPriceForDisplay = (intl, money) => {
  if (!money || typeof money.amount !== 'number' || !money.currency) {
    return '';
  }
  const hasCents = money.amount % 100 !== 0;
  return intl.formatNumber(money.amount / 100, {
    style: 'currency',
    currency: money.currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  });
};

const MINIMAL_PRICE_DIGITS_ONLY_REGEXP = /^\d+$/;

/**
 * True when a direct-contact minimal price (publicData.minimalPrice, a raw string) is digits only.
 *
 * @param {string} raw stored minimal price
 * @returns {boolean}
 */
export const isNumericMinimalPrice = raw =>
  typeof raw === 'string' && MINIMAL_PRICE_DIGITS_ONLY_REGEXP.test(raw.trim());

/**
 * Minimal price of a direct-contact service for display (card first line, map pin). Digits-only
 * values get the euro suffix («458 €»); text values («по договорённости») are returned as stored.
 * Storage is never altered here.
 *
 * @param {string} raw stored minimal price
 * @returns {string} display value, or '' when there is no minimal price
 */
export const formatMinimalPriceForDisplay = raw => {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return '';
  }
  return isNumericMinimalPrice(raw) ? `${raw.trim()} \u20AC` : raw;
};
